/**
 * First aid needs calculator (free tool, /free/first-aid-needs-calculator;
 * SEO plan Workstream D row 16 and Workstream E row 6).
 *
 * Pure logic, no React: a visitor's answers in, the fixed blocks of
 * provision to show out. Every block's words live here, so the page, the
 * guide and the safety review all read one set of text. Every line cites
 * where it comes from (read 31 Oct 2026, see ./sources.ts):
 *
 *  - Health and Safety (First-Aid) Regulations 1981: reg 3 (equipment,
 *    facilities and first-aiders; 3(4) an appointed person instead of a
 *    first-aider where the work, numbers and location make that adequate),
 *    reg 4 (tell employees), reg 5 (relevant self-employed person: own kit);
 *  - Prescribed Undertakings Regulations 2015, Schedule para 3: work on a
 *    construction site is prescribed, so reg 5 applies to the self-employed
 *    there;
 *  - HSE L74 (third edition, amended 2024). Appendix 3 lists construction as
 *    an example of HIGHER-hazard work, so the calculator always uses the
 *    higher-hazard column. Its numbers are "suggestions only" (para 54): the
 *    calculator says so on every result and never presents them as law;
 *  - CDM 2015 reg 30 (arrangements for foreseeable emergencies).
 *
 * The 5 to 50 band is where secondary sources disagree. The calculator
 * prints HSE's own words for it ("At least 1 EFAW or FAW trained
 * first-aider, depending on the type of injuries that may occur") and does
 * not choose between EFAW and FAW.
 *
 * Pinned path by path in __tests__/lib/marketing/first-aid-calculator/calculator.test.ts.
 */
import type { SourceKey } from "./sources";

export type YesNo = "yes" | "no";
export type WorkforceSize = "fewer-than-5" | "5-to-50" | "more-than-50";

export interface FirstAidAnswers {
  /** "yes" for an employer; "no" for a self-employed person working on their own. */
  employs: YesNo;
  /** Asked only of an employer. */
  size?: WorkforceSize;
  remote: YesNo;
  /** Asked only of an employer. */
  shifts?: YesNo;
  /** Asked only of an employer. */
  lone?: YesNo;
  shared: YesNo;
}

export type QuestionId = keyof FirstAidAnswers;

export interface QuestionOption {
  value: string;
  label: string;
}

export interface Question {
  id: QuestionId;
  label: string;
  hint?: string;
  options: readonly QuestionOption[];
}

const YES_NO: readonly QuestionOption[] = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];

// ---------------------------------------------------------------------------
// Questions (exact wording shown to the visitor)
// ---------------------------------------------------------------------------

export const Q_EMPLOYS: Question = {
  id: "employs",
  label: "Do you employ anyone who will work on this site?",
  hint: "Answer for your own business. If you are a company, your workers are employees.",
  options: [
    { value: "yes", label: "Yes, I (or my company) employ people who will work here" },
    { value: "no", label: "No, I am self-employed and work on my own" },
  ],
};

export const Q_SIZE: Question = {
  id: "size",
  label: "How many of your workers will be on site at the same time?",
  hint: "HSE's suggested numbers are for the people at work at any one time. If you run shifts, answer for your busiest shift. If you have agreed to provide first aid for other firms on a shared site, count their workers too.",
  options: [
    { value: "fewer-than-5", label: "Fewer than 5" },
    { value: "5-to-50", label: "5 to 50" },
    { value: "more-than-50", label: "More than 50" },
  ],
};

export const Q_REMOTE: Question = {
  id: "remote",
  label: "Is the site remote from emergency medical services?",
  hint: "For example, a rural or island site, or anywhere an ambulance could take a long time to reach.",
  options: YES_NO,
};

export const Q_SHIFTS: Question = {
  id: "shifts",
  label: "Will anyone work shifts, or outside normal working hours?",
  options: YES_NO,
};

export const Q_LONE: Question = {
  id: "lone",
  label: "Will any of your workers work alone, or travel between sites?",
  options: YES_NO,
};

export const Q_SHARED: Question = {
  id: "shared",
  label: "Will other firms or self-employed people work on the site at the same time?",
  options: YES_NO,
};

/** The questions to show for the answers so far, in order. */
export function questionsFor(answers: Partial<FirstAidAnswers>): Question[] {
  if (answers.employs === "no") return [Q_EMPLOYS, Q_REMOTE, Q_SHARED];
  return [Q_EMPLOYS, Q_SIZE, Q_REMOTE, Q_SHIFTS, Q_LONE, Q_SHARED];
}

/** True once every question that applies has an answer. */
export function isComplete(answers: Partial<FirstAidAnswers>): answers is FirstAidAnswers {
  return questionsFor(answers).every((question) => answers[question.id] !== undefined);
}

// ---------------------------------------------------------------------------
// Blocks (exact wording of every outcome)
// ---------------------------------------------------------------------------

export interface Line {
  text: string;
  source: SourceKey;
  /** The citation as printed, e.g. "L74 para 24" or "reg 3(4)". */
  cite: string;
}

export type BlockId =
  | "people-fewer-than-5"
  | "people-5-to-50"
  | "people-more-than-50"
  | "first-aiders"
  | "self-employed"
  | "kit"
  | "kit-self-employed"
  | "remote"
  | "shifts"
  | "lone"
  | "shared"
  | "tell-everyone"
  | "record";

export interface Block {
  id: BlockId;
  heading: string;
  lines: readonly Line[];
}

const l = (text: string, source: SourceKey, cite: string): Line => ({ text, source, cite });

/** HSE's own words in L74 Appendix 3 (higher hazard column), quoted on the result. */
export const HSE_SUGGESTION: Readonly<Record<WorkforceSize, string>> = {
  "fewer-than-5": "At least 1 appointed person",
  "5-to-50": "At least 1 EFAW or FAW trained first-aider, depending on the type of injuries that may occur",
  "more-than-50": "At least 1 FAW trained first-aider for every 50 employed (or part thereof)",
};

export const BLOCKS: Readonly<Record<BlockId, Block>> = {
  "people-fewer-than-5": {
    id: "people-fewer-than-5",
    heading: "First-aid people: at least 1 appointed person",
    lines: [
      l(`For higher-hazard work with fewer than 5 employees, HSE's suggested starting point is: "${HSE_SUGGESTION["fewer-than-5"]}".`, "l74", "L74 Appendix 3"),
      l("The appointed person takes charge of the first-aid arrangements, including looking after the equipment and facilities and calling the emergency services when required. HSE says an appointed person should be available to do this at all times when people are at work.", "l74", "L74 para 78"),
      l("An appointed person does not need first-aid training, though HSE says they may benefit from an emergency first aid at work (EFAW) course.", "l74", "L74 para 80"),
      l("There is still the possibility of an accident or sudden illness, so HSE says you may wish to provide qualified first-aiders instead of an appointed person.", "l74", "L74 para 80"),
      l("The law allows an appointed person instead of a first-aider only where the nature of the work, the number of employees and the location of the workplace make that adequate and appropriate.", "reg3", "reg 3(4)"),
    ],
  },
  "people-5-to-50": {
    id: "people-5-to-50",
    heading: "First-aid people: at least 1 trained first-aider",
    lines: [
      l(`For higher-hazard work with 5 to 50 employees, HSE's suggested starting point is: "${HSE_SUGGESTION["5-to-50"]}".`, "l74", "L74 Appendix 3"),
      l("EFAW training covers emergency first aid to someone who is injured or becomes ill at work. FAW training includes EFAW and also covers a range of specific injuries and illnesses, such as injuries to bones, muscles and joints, burns and scalds, eye injuries and major illness.", "l74", "L74 para 5 and Appendices 5 and 6"),
      l("HSE does not choose between EFAW and FAW for you. Look at the injuries your work could cause, and choose the level of training that covers them.", "l74", "L74 Appendix 3"),
    ],
  },
  "people-more-than-50": {
    id: "people-more-than-50",
    heading: "First-aid people: FAW trained first-aiders",
    lines: [
      l(`For higher-hazard work with more than 50 employees, HSE's suggested starting point is: "${HSE_SUGGESTION["more-than-50"]}". For example, 51 to 100 workers means at least 2.`, "l74", "L74 Appendix 3"),
      l("HSE says a first-aid room will usually be necessary on large construction sites. It should be easy to reach with a stretcher, clearly signposted, and a designated person should be responsible for it.", "l74", "L74 paras 47 and 48"),
    ],
  },
  "first-aiders": {
    id: "first-aiders",
    heading: "Training and cover",
    lines: [
      l("A first-aider must have had appropriate training and hold appropriate qualifications.", "reg3", "reg 3(2)"),
      l("HSE no longer approves first-aid training providers, so check that the provider you choose is competent.", "hse-employers", "HSE: what employers need to do"),
      l("Plan cover for holidays and sickness. Annual leave does not count as an exceptional absence: if your assessment says you need first-aiders, one should be available whenever the need arises.", "l74", "L74 para 81"),
      l("HSE lists construction, and working in remote areas, as examples where extra training in haemostatic dressings, wound packing and tourniquets for life-threatening bleeding may be relevant.", "l74", "L74 Appendix 4, Table 3"),
    ],
  },
  "self-employed": {
    id: "self-employed",
    heading: "Self-employed and working on your own",
    lines: [
      l("Work on a construction site is a prescribed undertaking, so as a self-employed person there you must provide, or make sure there is provided, the equipment you need to give first aid to yourself while at work.", "reg5", "reg 5 and SI 2015/1583 Schedule para 3"),
      l("Where you work on premises controlled by an employer, or alongside other self-employed people, each of you is responsible for your own first-aid provision. You can agree joint arrangements with the others.", "l74", "L74 para 85"),
      l("If you take on an employee, even for one job, the employer duties apply. Run this calculator again as an employer.", "reg3", "reg 3"),
    ],
  },
  kit: {
    id: "kit",
    heading: "First-aid kit",
    lines: [
      l("Provide at least one suitably stocked first-aid container for each work site, marked with a white cross on a green background. Large sites may need more than one.", "l74", "L74 paras 36 and 37"),
      l("There is no mandatory list of contents. HSE gives a suggested minimum for low-hazard work and points to British Standard BS 8599. Whatever kit you use, its contents should reflect your needs assessment.", "l74", "L74 Appendix 2"),
      l("Keep containers easy to reach, near hand washing where possible and protected from dust and damp. Check them often and restock soon after use.", "l74", "L74 paras 37 and 38"),
      l("If mains tap water is not readily available for washing wounds and eyes, provide at least one litre of sterile water or sterile saline in sealed, disposable containers.", "l74", "L74 para 40"),
      l("Your needs assessment may show you need extra items, for example foil blankets, haemostatic dressings, tourniquets, or shears that can cut through clothing.", "l74", "L74 para 39"),
    ],
  },
  "kit-self-employed": {
    id: "kit-self-employed",
    heading: "Your first-aid kit",
    lines: [
      l("There is no mandatory list of contents. HSE gives a suggested list for a travelling first-aid kit. Stock yours for the injuries your work could cause.", "l74", "L74 Appendix 2"),
      l("If mains tap water is not readily available for washing wounds and eyes, provide at least one litre of sterile water or sterile saline in sealed, disposable containers.", "l74", "L74 para 40"),
    ],
  },
  remote: {
    id: "remote",
    heading: "Because the site is remote",
    lines: [
      l("Make arrangements so that suitable transport is available to get a casualty to medical help.", "l74", "L74 para 24"),
      l("Check that the first-aid kit is adequate in case the emergency services are delayed.", "l74", "L74 para 24"),
      l("Tell the emergency services, in writing, where the site is and about any particular circumstances, including specific hazards.", "l74", "L74 para 24"),
      l("For people working in remote areas, consider special arrangements such as personal communicators and extra training.", "l74", "L74 para 21"),
    ],
  },
  shifts: {
    id: "shifts",
    heading: "Because people work shifts or out of hours",
    lines: [
      l("First-aid provision must be available whenever employees are at work. You may need separate arrangements for each shift.", "l74", "L74 para 22"),
      l("HSE's suggested numbers are for the people available at all times people are at work, so apply them to each shift.", "l74", "L74 Appendix 3"),
    ],
  },
  lone: {
    id: "lone",
    heading: "Because people work alone or travel",
    lines: [
      l("Decide whether workers who travel, or move between sites, should carry a personal first-aid kit.", "l74", "L74 para 21"),
      l("Make sure anyone working alone has a way to call for help in an emergency, such as a mobile phone.", "l74", "L74 para 21"),
    ],
  },
  shared: {
    id: "shared",
    heading: "Because other firms share the site",
    lines: [
      l("On a shared site, one employer can take responsibility for first-aid cover for all the workers. Share information about the hazards so the shared cover is adequate.", "l74", "L74 para 25"),
      l("All employers should agree the arrangements, and workers should be told about them. HSE strongly recommends a written agreement between the employers.", "l74", "L74 para 25"),
    ],
  },
  "tell-everyone": {
    id: "tell-everyone",
    heading: "Telling everyone",
    lines: [
      l("Tell your employees about the first-aid arrangements, including where the equipment, facilities and first-aid people are.", "reg4", "reg 4"),
      l("Put up at least one first-aid notice in a prominent place on each site, and include first aid in the site induction.", "l74", "L74 para 83"),
      l("On a construction site, where necessary, arrangements must be made for dealing with any foreseeable emergency, and everyone they cover must be familiar with them. Include first aid in those arrangements.", "cdm30", "CDM 2015 reg 30(1) and (3)"),
    ],
  },
  record: {
    id: "record",
    heading: "Writing it down",
    lines: [
      l("You do not have to write your needs assessment down, but HSE says keeping a record helps you show an inspector how you decided on your first-aid provision. L74 Appendix 1 is a record form you can use.", "l74", "L74 para 9 and Appendix 1"),
    ],
  },
};

export interface FirstAidResult {
  /** One line for the top of the result. */
  summary: string;
  blocks: BlockId[];
}

const SIZE_BLOCK: Readonly<Record<WorkforceSize, BlockId>> = {
  "fewer-than-5": "people-fewer-than-5",
  "5-to-50": "people-5-to-50",
  "more-than-50": "people-more-than-50",
};

const SIZE_SUMMARY: Readonly<Record<WorkforceSize, string>> = {
  "fewer-than-5": "At least 1 appointed person, and a stocked first-aid kit",
  "5-to-50": "At least 1 EFAW or FAW trained first-aider, and a stocked first-aid kit",
  "more-than-50": "At least 1 FAW trained first-aider for every 50 workers, and stocked first-aid kits",
};

/**
 * The blocks for a complete set of answers. Answers that do not apply to the
 * path (an employer-only question answered before switching to self-employed)
 * are ignored, so stale state can never change the result.
 */
export function assessFirstAid(answers: FirstAidAnswers): FirstAidResult {
  const blocks: BlockId[] = [];
  if (answers.employs === "no") {
    blocks.push("self-employed", "kit-self-employed");
    if (answers.remote === "yes") blocks.push("remote");
    if (answers.shared === "yes") blocks.push("shared");
    blocks.push("record");
    return { summary: "A first-aid kit for yourself", blocks };
  }
  const size = answers.size ?? "fewer-than-5";
  blocks.push(SIZE_BLOCK[size]);
  // Training and cover applies wherever HSE suggests a trained first-aider,
  // and also to a small employer who chooses first-aiders (L74 para 80).
  blocks.push("first-aiders", "kit");
  if (answers.remote === "yes") blocks.push("remote");
  if (answers.shifts === "yes") blocks.push("shifts");
  if (answers.lone === "yes") blocks.push("lone");
  if (answers.shared === "yes") blocks.push("shared");
  blocks.push("tell-everyone", "record");
  return { summary: SIZE_SUMMARY[size], blocks };
}

/** Shown above every result: these are HSE's suggestions, not a legal table. */
export const STARTING_POINT_LINE =
  "This is HSE's suggested starting point, from its guidance L74. Your needs assessment decides.";
export const HIGHER_HAZARD_LINE =
  "HSE's guidance lists construction as an example of higher-hazard work, so this calculator uses HSE's higher-hazard suggestions.";
/** Shown under every result and on the page. */
export const SCOPE_LINE =
  "This covers Great Britain. Northern Ireland has its own Health and Safety (First-Aid) Regulations (Northern Ireland) 1982 and CDM 2016 regulations.";
export const NOT_LEGAL_ADVICE = "This is general guidance, not legal advice.";
